import { BrandLoader } from '@/components/ui/loaders';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, ShieldAlert, X } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { FACILITY_LABELS, MODULE_CATALOGUE, type ModuleCode } from '@his/shared';
import { allowsPath, CLIENT_NAVIGATION, modulesForPath, permitsPath, PLATFORM_NAVIGATION, visibleSections } from '@/lib/navigation';
import { hidesShareScreens } from '@/features/accounts/fee-splitting';
import { BranchSwitcher } from './BranchSwitcher';
import { LanguageSwitch } from './LanguageSwitch';
import { UserMenu } from './UserMenu';
import { useSelectedBranch } from '@/lib/branch';
import { useScrollToProblem } from '@/lib/scroll-to-problem';
import { useTranslation } from '@/lib/i18n';
import { localiseSections } from '@/lib/navigation-i18n';
import { SOURCE_MESSAGES } from '@his/shared';
import { LicenseLockBanner } from './LicenseLockBanner';
import { LifecycleBanner } from './LifecycleBanner';
import { SupportSessionBanner } from './SupportSessionBanner';
import { OwnerWatchListener } from './OwnerWatchListener';
import { SignatureNoticeBanner } from './SignatureNoticeBanner';
import { EmailPausedBanner } from './EmailPausedBanner';
import { SidebarNav } from './SidebarNav';
import { ThemeToggle } from './ThemeToggle';
import { StaffAlertBell } from './StaffAlertBell';
import { UpdateBanner } from './UpdateBanner';
import { HelpProvider } from './help/HelpProvider';
import { HelpSheet } from './help/HelpSheet';
import { cn, initials } from '@/lib/utils';
import { Badge, Button } from './ui/primitives';
import { EmptyState, LockedIllustration } from './ui/layout';

export function AppShell() {
  const { user, loading, can, hasModule, worksIn, isRetired, needsTwoFactorSetup, mustChangePassword } = useAuth();
  const selectedBranchId = useSelectedBranch();
  const mainRef = useScrollToProblem();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const shellText = useTranslation('shell');
  const common = useTranslation('common');
  useEffect(() => {
    // Carry the wanted path along (LIMS-parity: typing /owner or /client cold
    // pre-selects the right sign-in door and returns there afterwards).
    if (!loading && !user) navigate('/login', { replace: true, state: { from: location.pathname } });
  }, [loading, user, navigate, location.pathname]);

  useEffect(() => {
    if (needsTwoFactorSetup) navigate('/two-factor', { replace: true });
  }, [needsTwoFactorSetup, navigate]);

  useEffect(() => {
    // A temporary password reaches one screen. The API refuses everything
    // else with 403 anyway; this just stops the shell from rendering a
    // dashboard full of failed requests first.
    if (!needsTwoFactorSetup && mustChangePassword) navigate('/change-password', { replace: true });
  }, [needsTwoFactorSetup, mustChangePassword, navigate]);

  useEffect(() => {
    // Owner-console staff live in their own surface now (/owner, LIMS-parity)
    // — the hospital shell's tenant screens would only 403 for them. Contact
    // people stay: their portal (/client) renders inside this shell.
    if (!loading && user?.isPlatformUser && user.platformRole !== 'contact_person') {
      navigate('/owner', { replace: true });
    }
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <BrandLoader fullScreen label="Loading your workspace…" />
    );
  }
  if (!user) return null;

  // Three shells, one layout. A contact person holds a platform token but is
  // not owner-console staff: their nav is the portal's, and the owner console's
  // routes would 403 for them anyway.
  const isContactPerson = user.platformRole === 'contact_person';
  const sections = user.isPlatformUser
    ? [{ title: isContactPerson ? 'Portal' : 'Platform', items: isContactPerson ? CLIENT_NAVIGATION : PLATFORM_NAVIGATION }]
    : visibleSections(can, worksIn, (item) => hidesShareScreens(user, item.to), isRetired);
  const shownSections =
    shellText.translated && shellText.locale !== 'en' && !user.isPlatformUser
      ? localiseSections(sections, (key) => (key in SOURCE_MESSAGES.shell ? shellText.t(key) : null))
      : sections;

  // Platform users are not on a hospital's plan at all, so a module check does
  // not apply to them.
  const blocked = !user.isPlatformUser && !allowsPath(location.pathname, hasModule, isRetired);
  const blockedModules = blocked ? (modulesForPath(location.pathname) ?? []) : [];
  const outsideRole = !user.isPlatformUser && !blocked && !allowsPath(location.pathname, worksIn, isRetired);
  const outsideRoleModules = outsideRole ? (modulesForPath(location.pathname) ?? []) : [];
  const notPermitted = !user.isPlatformUser && !blocked && !outsideRole && !permitsPath(location.pathname, can);

  return (
    <div className="flex h-dvh overflow-hidden bg-muted/30 print:block print:h-auto print:overflow-visible">
      <aside
        className={cn(
          // Sticky and viewport-high on desktop: the nav scrolls inside it and
          // the profile strip stays put. `lg:static` let the aside grow with
          // the page instead, so a long menu pushed the profile off-screen.
          'fixed inset-y-0 start-0 z-40 flex w-64 shrink-0 flex-col border-e bg-card transition-transform lg:static lg:h-full lg:translate-x-0 no-print',
          menuOpen ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full',
        )}
      >
        <div className="flex h-14 items-center justify-between border-b px-4">
          <Link to={user.isPlatformUser ? (isContactPerson ? '/client' : '/owner') : '/dashboard'} className="flex items-center gap-2 font-semibold">
            <span className="grid h-7 w-7 place-items-center rounded bg-primary text-xs text-primary-foreground">
              HIS
            </span>
            <span className="truncate text-sm" title={user.tenantName ?? undefined}>
              {isContactPerson
                ? 'Client portal'
                : user.isPlatformUser
                  ? 'Owner console'
                  : (user.tenantName ?? FACILITY_LABELS[user.facilityType])}
            </span>
          </Link>
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMenuOpen(false)} aria-label={common.t('nav.close_menu')}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <SidebarNav sections={shownSections} onNavigate={() => setMenuOpen(false)} />

        <div className="border-t p-3">
          <div className="flex items-center gap-2.5 rounded-lg px-1.5 py-1">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
              {initials(user.displayName)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user.displayName}</p>
              <p className="truncate text-xs text-muted-foreground">
                {user.isPlatformUser ? user.platformRole : user.roles.map((r) => r.name).join(', ') || 'No role'}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {menuOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMenuOpen(false)} aria-hidden />
      )}

      <HelpProvider enabled={!user.isPlatformUser}>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col print:block">
          <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-card/95 px-3 backdrop-blur no-print lg:px-4">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMenuOpen(true)} aria-label={common.t('nav.open_menu')}>
                <Menu className="h-4 w-4" />
              </Button>
              {user.tenantId && <BranchSwitcher />}
            </div>
            <div className="flex shrink-0 items-center justify-end gap-1">
              {user.tenantId && <LanguageSwitch />}
              {user.tenantId && !user.has2fa && user.requires2fa && (
                <Badge variant="warning" className="hidden gap-1 sm:inline-flex">
                  <ShieldAlert className="h-3 w-3" /> 2FA required
                </Badge>
              )}
              {user.tenantId && <StaffAlertBell />}
              <ThemeToggle />
              <UserMenu />
            </div>
          </header>

          <SupportSessionBanner />
          <OwnerWatchListener />
          <LicenseLockBanner />
          <LifecycleBanner />
          <UpdateBanner />
          <SignatureNoticeBanner />
          <EmailPausedBanner />
          <main ref={mainRef} className="app-scroll relative min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain p-4 lg:p-6 print:overflow-visible">
            {/* The sidebar already hides what this hospital has not licensed, but
                a bookmark, a link in an email or a typed URL walked straight into
                the screen anyway — which then failed against the API with a
                module_disabled the page had no way to explain. */}
            {blocked ? (
              <ModuleNotLicensed modules={blockedModules} />
            ) : outsideRole ? (
              <OutsideYourRole modules={outsideRoleModules} />
            ) : notPermitted ? (
              <NotPermitted />
            ) : (
              <Outlet key={selectedBranchId ?? 'all'} />
            )}
          </main>
        </div>
        <HelpSheet />
      </HelpProvider>
    </div>
  );
}

/**
 * What a screen shows when the hospital has not licensed the module behind it.
 *
 * A plain "not found" would read as a broken link; the honest answer is that
 * the feature exists and this hospital does not have it, and that switching it
 * on is the owner's decision rather than something the staff can fix.
 */
function ModuleNotLicensed({ modules }: { modules: ModuleCode[] }) {
  const named = modules
    .map((code) => MODULE_CATALOGUE.find((m) => m.code === code)?.name ?? code)
    .filter((name, index, all) => all.indexOf(name) === index);

  // A screen that serves several modules — patient records serve a dozen — is
  // available as soon as the hospital has any one of them, so the message says
  // so rather than naming a single module the operator may not want.
  const title =
    named.length <= 1
      ? `${named[0] ?? 'This module'} is not part of your plan`
      : 'This screen is not part of your plan';
  const description =
    named.length <= 1
      ? (MODULE_CATALOGUE.find((m) => m.code === modules[0])?.description ??
          'Ask your administrator to have this module enabled.')
      : `It comes with any of: ${named.slice(0, 6).join(', ')}${named.length > 6 ? ' and others' : ''}.`;

  return (
    <EmptyState
      illustration={<LockedIllustration />}
      title={title}
      description={`${description} Ask your administrator to have it enabled.`}
      action={
        <Link to="/dashboard">
          <Button variant="outline">Back to the dashboard</Button>
        </Link>
      }
    />
  );
}

function NotPermitted() {
  const common = useTranslation('common');
  return (
    <EmptyState
      illustration={<LockedIllustration />}
      title={common.t('nav.no_access_title')}
      description={common.t('nav.no_access_body')}
      action={
        <Link to="/" className="inline-flex h-9 items-center rounded-md border px-4 text-sm font-medium hover:bg-muted focus-ring">
          {common.t('nav.go_dashboard')}
        </Link>
      }
    />
  );
}

function OutsideYourRole({ modules }: { modules: ModuleCode[] }) {
  const named = modules
    .map((code) => MODULE_CATALOGUE.find((m) => m.code === code)?.name ?? code)
    .filter((name, index, all) => all.indexOf(name) === index);
  return (
    <EmptyState
      illustration={<LockedIllustration />}
      title={named.length === 1 ? `${named[0]} is outside your role` : 'This screen is outside your role'}
      description="Your role is limited to its own department's screens. Ask an administrator if your work needs this one."
      action={
        <Link to="/dashboard" className="inline-flex h-9 items-center rounded-md border px-4 text-sm font-medium hover:bg-muted focus-ring">
          Back to the dashboard
        </Link>
      }
    />
  );
}
